import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Alert, Badge, Button } from '@react/components';
import type { Task } from '@core/types/task';
import { TaskReference } from '@/features/references/TaskReference';
import { ActorReference } from '@/features/references/ActorReference';
import { getEntityId, getStatusMapping, getTaskAssignee, getTaskStatus, taskStatusMap } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';
import { loadDocumentTasks, mergeDocumentTasks } from './documentTasks';

interface DocumentTasksSectionProps {
  documentId: string;
  refreshKey?: number;
}

export function DocumentTaskList({ tasks }: { tasks: Task[] }) {
  const { t } = useI18n();
  return (
    <div className="divide-y divide-border">
      {tasks.map((task) => {
        const id = getEntityId(task);
        const status = getTaskStatus(task);
        const assignee = getTaskAssignee(task);
        return (
          <div key={id} className="flex items-center justify-between gap-3 py-2.5">
            <div className="flex min-w-0 items-center gap-2">
              {status && (
                <Badge variant={getStatusMapping(taskStatusMap, status).variant} size="sm">
                  {t(getStatusMapping(taskStatusMap, status).labelKey)}
                </Badge>
              )}
              <TaskReference id={id} fallbackTitle={task.title} className="text-sm" />
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">
              {assignee ? <ActorReference actor={assignee} /> : t('task.unassigned')}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export const DocumentTasksSection = observer(({ documentId, refreshKey = 0 }: DocumentTasksSectionProps) => {
  const { t } = useI18n();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [nextCursor, setNextCursor] = useState('');
  const [hasNext, setHasNext] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async (cursor = '', append = false) => {
    const request = ++requestRef.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const page = await loadDocumentTasks(documentId, cursor);
      if (request !== requestRef.current) return;
      setTasks((current) => append ? mergeDocumentTasks(current, page.tasks) : page.tasks);
      setNextCursor(page.nextCursor);
      setHasNext(page.hasNext);
    } catch (cause) {
      if (request !== requestRef.current) return;
      setError(cause);
      if (!append) {
        setTasks([]);
        setNextCursor('');
        setHasNext(false);
      }
    } finally {
      if (request === requestRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [documentId]);

  useEffect(() => {
    void load();
    return () => { requestRef.current += 1; };
  }, [documentId, refreshKey, load]);

  return (
    <section className="mt-8 border-t border-border pt-5" aria-labelledby="document-tasks-heading">
      <div className="mb-3 flex items-center gap-2">
        <h2 id="document-tasks-heading" className="text-sm font-semibold text-foreground">
          {t('document.tasks.title')}
        </h2>
        {!loading && tasks.length > 0 && (
          <span className="text-xs tabular-nums text-muted-foreground">{tasks.length}</span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto"
          disabled={loading || loadingMore}
          onClick={() => void load()}
        >
          {t('document.tasks.refresh')}
        </Button>
      </div>

      {error != null && (
        <Alert variant="error">
          <div className="flex flex-wrap items-center gap-3">
            <span>{errMsg(error, t('document.tasks.loadError'))}</span>
            <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
              {t('document.tasks.retry')}
            </Button>
          </div>
        </Alert>
      )}

      {loading ? (
        <div className="space-y-2" aria-busy="true" aria-label={t('common.loading')}>
          <div className="h-9 animate-pulse rounded-md bg-muted" />
          <div className="h-9 animate-pulse rounded-md bg-muted" />
        </div>
      ) : error == null && tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('document.tasks.empty')}</p>
      ) : (
        <DocumentTaskList tasks={tasks} />
      )}

      {hasNext && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="mt-3"
          isLoading={loadingMore}
          onClick={() => void load(nextCursor, true)}
        >
          {t('document.tasks.loadMore')}
        </Button>
      )}
    </section>
  );
});

export default DocumentTasksSection;
